import { Inject, Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable, of } from 'rxjs'
import { FloListResponse } from '@flocasts/flosports30-types/dist/flo-rest-api/response'
import { Article, RankingContainer, ResultContainer, Video } from '@flocasts/flosports30-types/dist/entity'
import { LOGGER_SERVICE } from '../../logger/logger.config'
import { LoggerService } from '../../logger/logger.interface'
import { catchError } from 'rxjs/operators'
import { EXPERIENCE_API_LEGACY_CORE_NODES_PATH, EXPERIENCE_API_VERSION } from 'src/app/app.config'

type ArticleVideoResultRanking = Article | Video | ResultContainer | RankingContainer
type ArticleVideo = Article | Video

const empty = { data: [], meta: {} }

type RequestedTypes = 'article,video' | 'article,ranking,result,video' | 'ranking'

type RelatedContentResponse<Types extends RequestedTypes> = Types extends 'article,video'
  ? FloListResponse<ArticleVideo, {}>
  : Types extends 'article,ranking,result,video'
  ? FloListResponse<ArticleVideoResultRanking, {}>
  : never

@Injectable({
  providedIn: 'root'
})
export class RelatedContentDataService {
  // prettier-ignore
  constructor(
    @Inject(LOGGER_SERVICE) private readonly log: LoggerService,
    private readonly http: HttpClient
  ) {
  }

  /**
   * Fetch related content for a specific node, specifying the kinds of content to return.
   *
   * @remarks
   * The `types` argument will control which types are returned. If you pass
   * `'article,video'` the response will contain Articles and Videos, and the
   * types will reflect that.
   *
   * @see {@link https://www.javiercasas.com/articles/typescript-dependent-types}
   */
  public getRelatedContent<T extends RequestedTypes>(
    nodeId: number,
    fallbackId: number | null,
    limit: number,
    types: T,
    excludeId?: number
  ): Observable<RelatedContentResponse<T>>
  public getRelatedContent(
    nodeId: number,
    fallbackId: number | null,
    limit: number,
    types: RequestedTypes,
    excludeId?: number
  ): Observable<RelatedContentResponse<typeof types>> {
    const params: Record<string, string | string[]> = {
      limit: limit.toString(10),
      types,
      ...(!!excludeId && { excludedNodeIds: excludeId.toString() }),
      version: EXPERIENCE_API_VERSION
    }

    return this.http
      .get<RelatedContentResponse<typeof types>>(`${EXPERIENCE_API_LEGACY_CORE_NODES_PATH}/${nodeId}/related`, {
        params
      })
      .pipe(
        catchError(_ => {
          if (fallbackId !== null) {
            return this.http.get<RelatedContentResponse<typeof types>>(
              `${EXPERIENCE_API_LEGACY_CORE_NODES_PATH}/${fallbackId}/related`,
              {
                params: {
                  limit: limit.toString(),
                  types,
                  version: EXPERIENCE_API_VERSION
                }
              }
            )
          }
          return of(empty)
        }),
        catchError(error => {
          this.log.error('Failed to fetch related content for fallback id.', {
            error,
            fallbackId
          })
          return of(empty)
        })
      )
  }

  /** Get Articles, Videos, Results, and Rankings related to a provided content ID. */
  public getArticlesVideosResultsRankings(
    nodeId: number,
    selfId?: number,
    fallbackId: number | null = null,
    limit = 3
  ): Observable<FloListResponse<ArticleVideoResultRanking, {}>> {
    return this.getRelatedContent(nodeId, fallbackId, limit, 'article,ranking,result,video', selfId)
  }

  /** Get just Articles and Videos */
  public getArticlesVideos(
    nodeId: number,
    selfId?: number,
    fallbackId: number | null = null,
    limit = 9
  ): Observable<FloListResponse<ArticleVideo, {}>> {
    return this.getRelatedContent(nodeId, fallbackId, limit, 'article,video', selfId)
  }

  /** Get just related Rankings */
  public getRankings(
    nodeId: number,
    selfId?: number,
    fallbackId: number | null = null,
    limit = 3
  ): Observable<FloListResponse<RankingContainer, {}>> {
    return this.getRelatedContent(nodeId, fallbackId, limit, 'ranking', selfId)
  }
}
